import { isNil, isNotNil } from '@flowio/is';
import { elementClasses } from '../../utilities/commonStyles';
import { createPaymentButton, removeElement, querySelectorAll } from '../../utilities/elementHelpers';
import { elIds, uiClasses } from './ui-constants';

function getKlarnaPMContainerId(index: number): string {
  return `${uiClasses.pm_container}-${index}`;
}

function isHTMLInputElement(element: EventTarget): element is HTMLInputElement {
  const castElement = (element as HTMLInputElement);
  return isNotNil(castElement.nodeName) && castElement.nodeName.toLowerCase() === 'input';
}

function selectItem(index: number, categoryId: string): void {
  querySelectorAll(`.${uiClasses.pm_container}`).forEach((element) => {
    element.classList.add('hidden');
  });
  const pmContainer = document.querySelector<HTMLDivElement>(`#${getKlarnaPMContainerId(index)}`);
  if (isNotNil(pmContainer)) {
    pmContainer.classList.remove('hidden');
    Klarna.Payments.load(
      {
        container: pmContainer,
        payment_method_category: categoryId,
      },
      (response) => {
        if (!response.show_form) {
          pmContainer.innerHTML = 'Error: PAYMENT_METHOD_CATEGORY_NOT_SUPPORTED';
        } else if (response.error) {
          pmContainer.innerHTML = 'Error: PAYMENT_METHOD_CATEGORY_NOT_SUPPORTED';
        }
      },
    );
  } else {
    throw new Error('Klarna selectItem: pmContainer is null');
  }
}

function klarnaRadioButtonChangeHandler(e: Event): void {
  const radioButton = e.currentTarget;
  if (isNotNil(radioButton) && isHTMLInputElement(radioButton)) {
    const { checked } = radioButton;
    if (checked === true) {
      const radioIndex = radioButton.dataset.index;
      if (isNotNil(radioIndex)) {
        selectItem(Number(radioIndex), radioButton.value);
      }
    }
  }
}

export function removeKlarnaChoiceUI(container: HTMLElement): void {
  const radioContainer = container.querySelector<HTMLElement>(`#${elIds.klarna_radio_container}`);
  if (isNotNil(radioContainer)) {
    removeElement(radioContainer);
  }
}

export function removeKlarnaContinueButton(container: HTMLElement): void {
  const button = container.querySelector<HTMLElement>(`.${uiClasses.klarna_continue_button}`);
  if (isNotNil(button)) {
    removeElement(button);
  }
}

export function renderKlarnaChoiceUI(
  container: HTMLElement,
  action: io.flow.v0.models.ActionUseSdkKlarnaV1,
): void {
  // clean up old event handlers if they exist.
  querySelectorAll(`input.${uiClasses.radio}`).forEach(
    (element) => element.removeEventListener('change', klarnaRadioButtonChangeHandler),
  );

  const oldRadioContainer = document.getElementById(elIds.klarna_radio_container);
  if (oldRadioContainer !== null) {
    removeElement(oldRadioContainer);
  }
  const radioContainer = document.createElement('div');
  radioContainer.id = elIds.klarna_radio_container;
  container.appendChild(radioContainer);

  const radioButtonsHtml = action.payment_method_categories.map((category, i) => `
    <div class="${uiClasses.category_container}">
      <div class="${uiClasses.radio_container} ${elementClasses.radio}">
        <input type="radio" id="radio-button.${i}" data-index="${i}" class="${uiClasses.radio}" name="klarna_payment_method_category" value="${category.id}"${i === 0 ? ' checked' : ''} />
        <label class="${uiClasses.radio_label}" for="radio-button.${i}">
          ${category.name}
        </label>
      </div>
      <div id="${getKlarnaPMContainerId(i)}" class="${uiClasses.pm_container} hidden"></div>
    </div>
  `);

  radioContainer.innerHTML = radioButtonsHtml.join('');

  querySelectorAll(`input.${uiClasses.radio}`).forEach(
    (element) => element.addEventListener('change', klarnaRadioButtonChangeHandler),
  );

  // select first item by default
  selectItem(0, action.payment_method_categories[0].id);
}

type KlarnaCallback = (response: KlarnaSDK.AuthorizeResponse | KlarnaSDK.FinalizeResponse) => void;

export function renderKlarnaContinueButton(
  buttonEl: HTMLElement,
  handleKlarnaAuthorize: (paymentCategory: string) => KlarnaCallback,
): void {
  const continueButton = createPaymentButton(buttonEl, { text: 'Continue', classNames: [uiClasses.klarna_continue_button] });
  continueButton.addEventListener('click', () => {
    const selectedRadio = document.querySelector<HTMLInputElement>(`input.${uiClasses.radio}:checked`);
    if (isNil(selectedRadio)) {
      throw new Error('handleKlarnaV1Action: selected Klarna category could not be detected.');
    }
    Klarna.Payments.authorize({
      payment_method_category: selectedRadio.value,
    }, handleKlarnaAuthorize(selectedRadio.value));
  });
}
